// react_frontend/src/Components/Partials/Navber.js
import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { MotherContext } from "../../Context/MotherContext";
import { permissions } from "../Router/permissions";

import kora from "../../Assets/Media/Logo.png";
import koraFav from "../../Assets/Media/lumen-logo.svg";

// Icons
import {
  FaBars,
  FaSignOutAlt,
  FaIdBadge,
  FaBell,
  FaSearch,
  FaChevronDown,
  FaCheckCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaClock,
  FaUserCheck,
  FaMoneyBillWave,
  FaCalendarCheck,
  FaUserPlus, FaClipboardList, FaDollarSign, FaSync, FaUsers, FaHandshake, FaTrophy
} from "react-icons/fa";

// Assets
import Logo from "../../Assets/Media/Logo.png";

// API
import { AxiosAuthInstance } from "../../AxiosInterceptors";

const TopNavbar = () => {
  const navigate = useNavigate();
  const { user, company } = useContext(MotherContext);

  // Panel states
  const [userOpen, setUserOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [notifError, setNotifError] = useState(null);

  const role = parseInt(localStorage.getItem("who_am_i"), 10);
  const can = permissions[role] || {};

  // Helper to get notification icon and color based on type
  const getNotifConfig = (type) => {
    switch (type) {
      case "leave_request": return { Icon: FaCalendarCheck, tint: "#6366f1" };
      case "leave_approved": return { Icon: FaCheckCircle, tint: "#10b981" };
      case "leave_rejected": return { Icon: FaExclamationTriangle, tint: "#ef4444" };
      case "payslip": return { Icon: FaMoneyBillWave, tint: "#f59e0b" };
      case "attendance": return { Icon: FaUserCheck, tint: "#3b82f6" };
      case "requisition": return { Icon: FaClipboardList, tint: "#8b5cf6" };
      case "requisition_approved": return { Icon: FaCheckCircle, tint: "#10b981" };
      case "requisition_rejected": return { Icon: FaExclamationTriangle, tint: "#ef4444" };
      case "salary_increment": return { Icon: FaDollarSign, tint: "#059669" };
      case "salary_increment_approved": return { Icon: FaCheckCircle, tint: "#10b981" };
      case "salary_increment_rejected": return { Icon: FaExclamationTriangle, tint: "#ef4444" };
      case "task_assigned": return { Icon: FaUserCheck, tint: "#3b82f6" }; 
      case "task_status_updated": return { Icon: FaSync, tint: "#8b5cf6" };
      case "crm_lead_assigned": return { Icon: FaUsers, tint: "#3b82f6" };
      case "crm_deal_assigned": return { Icon: FaHandshake, tint: "#8b5cf6" };
      case "crm_deal_won": return { Icon: FaTrophy, tint: "#10b981" };
      case "crm_activity_reminder": return { Icon: FaCalendarAlt, tint: "#f59e0b" };
      default: return { Icon: FaInfoCircle, tint: "#6b7280" };
    }
  };

  // Load notifications from API
  const loadNotifications = async () => {
    setLoadingNotifs(true);
    setNotifError(null);
    try {
      const res = await AxiosAuthInstance.get("notifications");
      // Laravel paginate returns { data: [...], current_page: 1, ... }
      const notifData = res.data.notifications?.data || res.data.notifications || [];
      setNotifications(notifData);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setNotifError("Failed to load notifications");
    } finally {
      setLoadingNotifs(false);
    }
  };

  // Mark single notification as read
  const markOneRead = async (id) => {
    try {
      await AxiosAuthInstance.post(`notifications/${id}/read`);
      
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );

      // Navigate if has action URL
      const notif = notifications.find((n) => n.id === id);
      if (notif?.action_url) {
        closeAllPanels();
        navigate(notif.action_url);
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  // Mark all notifications as read
  const markAllRead = async () => {
    const result = await Swal.fire({
      title: "Mark all as read?",
      text: "This will mark all notifications as read",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, mark all",
    });

    if (!result.isConfirmed) return;

    try {
      await AxiosAuthInstance.post("notifications/read-all");
      
      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      
      Swal.fire({
        icon: "success",
        title: "Done!",
        text: "All notifications marked as read",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  // Load notifications on mount and poll every 60 seconds
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  /* ============================================================
     SEARCH - Client-side, permission-aware
     ============================================================ */
  const buildSearchIndex = () => {
    const items = [
      { to: role === 4 ? "/hr/employee-dashboard" : "/dashboard", label: "Dashboard" },
      { to: "/profile", label: "My Profile" },
    ];
    if (can.hr?.includes("employee")) items.push({ to: "/hr/employees", label: "HR — Employees" });
    if (can.hr?.includes("attendance")) {
      items.push({ to: "/hr/attendance", label: "HR — Attendance Punch" });
      items.push({ to: "/hr/attendance/summary", label: "HR — Attendance Summary" });
    }
    if (can.hr?.includes("leave")) {
      items.push({ to: "/hr/leave/apply", label: "HR — Apply Leave" });
      if (role <= 2) items.push({ to: "/hr/leave/approval", label: "HR — Leave Approval" });
    }
    if (can.hr?.includes("payroll")) {
      items.push({ to: "/hr/payroll", label: "HR — Payroll" });
      items.push({ to: "/hr/payslips", label: "HR — Payslips" });
    }
    if (can.hr?.includes("assets")) items.push({ to: "/hr/assets", label: "HR — Assets" });
    if (can.hr?.includes("expenses")) items.push({ to: "/hr/expenses", label: "HR — Expenses" });
    if (can.hr?.includes("roster")) items.push({ to: "/hr/roster", label: "HR — Roster" });
    if (can.hr?.includes("documents")) items.push({ to: "/hr/documents", label: "HR — Documents" });
    if (can.hr?.includes("letters")) items.push({ to: "/hr/letters", label: "HR — Letters" });
    if (can.hr?.includes("recruitment")) items.push({ to: "/hr/recruitment", label: "HR — Recruitment" });
    if (can.hr?.includes("performance")) items.push({ to: "/hr/performance", label: "HR — Performance" });
    if (can.hr?.includes("ai_chat")) items.push({ to: "/hr/ai-chat", label: "HR — AI Assistant" });
    if (can.hr?.includes("requisition")) {
      items.push({ to: "/hr/requisition", label: "HR — Requisition" });
      if (role <= 2) items.push({ to: "/hr/requisition-approval", label: "HR — Requisition Approval" });
    }
    if (can.hr?.includes("salary_increment")) items.push({ to: "/hr/salary-increment", label: "HR — Salary Increment" });
    if (can.users?.includes("user")) items.push({ to: "/user_setting", label: "Users — User Setting" });
    if (can.users?.includes("salary")) items.push({ to: "/salary_setting", label: "Users — Salary Setting" });
    if (can.category?.includes("add_category")) items.push({ to: "/add_category", label: "Category — Add Category" });
    if (can.category?.includes("view_category")) items.push({ to: "/category", label: "Category — Category List" });
    if (can.items) {
      items.push({ to: "/add_item", label: "Items — Add Item" });
      items.push({ to: "/item_list", label: "Items — Item List" });
    }
    if (can.products) {
      items.push({ to: "/add_product", label: "Products — Add Product" });
      items.push({ to: "/product_list", label: "Products — Product List" });
    }
    if (can.inventory) {
      items.push({ to: "/purchase_list", label: "Inventory — Purchase List" });
      items.push({ to: "/stock_out", label: "Inventory — Stock Out" });
      items.push({ to: "/stock_in", label: "Inventory — Return Goods" });
      items.push({ to: "/stock_list", label: "Inventory — Stock List" });
      items.push({ to: "/today", label: "Inventory — Today" });
    }
    if (can.billing) {
      items.push({ to: "/add_billing", label: "Billing — Add Bill" });
      items.push({ to: "/billing_list", label: "Billing — Billing List" });
    }
    if (can.reports) items.push({ to: "/reports", label: "Reports" });
    if (can.customers) items.push({ to: "/customers", label: "Customers" });
    if (can.companySetting) {
      items.push({ to: "/company_setting", label: "Settings" });
      items.push({ to: "/activity_log", label: "Activity Log" });
    }
    return items;
  };

  const searchIndex = buildSearchIndex();
  const q = query.trim().toLowerCase();
  const results = q
    ? searchIndex.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 8)
    : [];

  /* ============ PANEL CONTROL ============ */
  const closeAllPanels = () => {
    setUserOpen(false);
    setNotifOpen(false);
    setSearchOpen(false);
  };

  const togglePanel = (setter, current) => {
    closeAllPanels();
    if (!current) setter(true);
  };

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  // Escape closes all panels
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAllPanels();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Mark body as "authenticated layout" so CSS offset only applies here
  useEffect(() => {
    document.body.classList.add("app-layout");
    return () => document.body.classList.remove("app-layout");
  }, []);

  const goTo = (to) => {
    closeAllPanels();
    setQuery("");
    navigate(to);
  };

  /* ============ SIDEBAR TOGGLE ============ */
  const toggleSidebar = () => {
    if (window.innerWidth <= 992) {
      document.body.classList.toggle("sb-mobile-open");
    } else {
      document.body.classList.toggle("sb-mini");
    }
  };

  useEffect(() => {
    if (window.innerWidth > 992) {
      document.body.classList.add("sb-mini");
    }
  }, []);

  /* ============ LOGOUT ============ */
  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out from the system!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Logout!",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        AxiosAuthInstance.post("logout").catch(console.warn);
        localStorage.clear();
        sessionStorage.clear();
        document.cookie.split(";").forEach((c) => {
          document.cookie = c
            .replace(/^ +/, "")
            .replace("=", "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.location.href = "/";
      }
    });
  };

  /* ============ LOGO & ROLE ============ */
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  const logoPath = company?.company_logo
    ? company.company_logo.replace(/\\/g, "/").split(" ").join("%20")
    : null;
  const logoUrl = logoPath ? `${apiBaseUrl}/${logoPath}` : Logo;

  const getRoleName = () => {
    switch (String(user?.role).trim()) {
      case "1": return "Master Admin";
      case "2": return "Admin";
      case "3": return "Account";
      default: return "Sales Manager";
    }
  };

  return (
    <nav className="modern-navbar">
      <div className="navbar-container">
        {/* ===== Left ===== */}
        <div className="navbar-left">
          <button type="button" className="nav-icon-btn" onClick={toggleSidebar} title="Toggle Sidebar">
            <FaBars />
          </button>

          <Link to="/dashboard" className="navbar-brand">
            <img
              src={kora}
              alt="Logo"
              onError={(e) => { e.target.onerror = null; e.target.src = Logo; }}
              className="brand-logo"
            />
            <span className="brand-name">{company?.company_name || ""}</span>
          </Link>
        </div>

        {/* ===== Right ===== */}
        <div className="navbar-right">
          {/* ---- SEARCH ---- */}
          <div className="navbar-panel-wrapper">
            <button
              type="button"
              className="nav-icon-btn"
              title="Search pages"
              onClick={() => togglePanel(setSearchOpen, searchOpen)}
            >
              <FaSearch />
            </button>

            {searchOpen && (
              <>
                <div className="dropdown-backdrop" onClick={closeAllPanels} />
                <div className="panel-dropdown search-dropdown">
                  <div className="search-box">
                    <FaSearch className="search-icon" />
                    <input
                      ref={searchInputRef}
                      className="search-input"
                      placeholder="Search pages… e.g. payroll"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                  <div className="search-results">
                    {q === "" ? (
                      <p className="search-hint">Type to search menus & pages…</p>
                    ) : results.length > 0 ? (
                      results.map((r) => (
                        <button
                          key={r.to}
                          type="button"
                          className="search-result-item"
                          onClick={() => goTo(r.to)}
                        >
                          <FaSearch className="result-icon" />
                          <span>{r.label}</span>
                        </button>
                      ))
                    ) : (
                      <p className="search-hint">No results for "{query}"</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ---- NOTIFICATIONS ---- */}
          <div className="navbar-panel-wrapper">
            <button
              type="button"
              className="nav-icon-btn"
              title="Notifications"
              onClick={() => togglePanel(setNotifOpen, notifOpen)}
            >
              <FaBell />
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            {notifOpen && (
              <>
                <div className="dropdown-backdrop" onClick={closeAllPanels} />
                <div className="panel-dropdown notif-dropdown">
                  <div className="notif-header">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <button type="button" className="mark-read" onClick={markAllRead}>
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="notif-list">
                    {loadingNotifs ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading notifications...</p>
                      </div>
                    ) : notifError ? (
                      <div className="error-state">
                        <FaExclamationTriangle className="error-icon" />
                        <p>{notifError}</p>
                        <button type="button" className="retry-btn" onClick={loadNotifications}>
                          Retry
                        </button>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="empty-state">
                        <FaBell className="empty-icon" />
                        <p>You're all caught up!</p>
                        <p className="subtext">No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((n) => {
                        const { Icon, tint } = getNotifConfig(n.type);
                        return (
                          <button
                            key={n.id}
                            type="button"
                            className={`notif-item ${!n.read_at ? "unread" : ""}`}
                            onClick={() => markOneRead(n.id)}
                          >
                            <div className="notif-icon-wrapper" style={{ backgroundColor: `${tint}14` }}>
                              <Icon className="notif-icon" style={{ color: tint }} />
                            </div>
                            <div className="notif-body">
                              <span className="notif-title">{n.title}</span>
                              <span className="notif-text">{n.message}</span>
                              <span className="notif-time">
                                <FaClock size={10} /> {new Date(n.created_at).toLocaleString()}
                              </span>
                            </div>
                            {!n.read_at && <span className="notif-dot" />}
                          </button>
                        );
                      })
                    )}
                  </div>

                  {notifications.length > 0 && (
                    <div className="notif-footer">
                      <button type="button" className="clear-all" onClick={() => { setNotifications([]); }}>
                        Clear all notifications
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ---- USER MENU ---- */}
          <div className={`user-dropdown-wrapper ${userOpen ? "open" : ""}`}>
            <button
              type="button"
              className="user-trigger"
              onClick={() => togglePanel(setUserOpen, userOpen)}
            >
              <div className="user-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="user-info-brief">
                <span className="user-name">{user?.name || "User"}</span>
                <span className="user-role">{getRoleName()}</span>
              </div>
              <FaChevronDown className="chevron" />
            </button>

            {userOpen && (
              <>
                <div className="dropdown-backdrop" onClick={closeAllPanels} />
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <h4 className="dropdown-name">{user?.name || "User"}</h4>
                      <p className="dropdown-role">{getRoleName()}</p>
                    </div>
                  </div>

                  <div className="dropdown-stats">
                    <div className="stat-item">
                      <span className="stat-label">Package</span>
                      <span className="stat-value">{company?.package || "Standard"}</span>
                    </div>
                  </div>

                  <div className="dropdown-divider" />

                  <div className="dropdown-actions">
                    <Link to="/profile" className="dropdown-item primary" onClick={closeAllPanels}>
                      <FaIdBadge /> <span>My Profile</span>
                    </Link>
                    <button type="button" className="dropdown-item danger" onClick={handleLogout}>
                      <FaSignOutAlt /> <span>Logout</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNavbar;